'use client';

import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import { Button } from '@carina/components/buttons';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { twMerge } from 'tailwind-merge';
import CarouselDot from './carousel-dot.component';

export interface GenericCarouselProps {
  options?: EmblaOptionsType;
  arrows?: boolean;
  fadeEdges?: boolean;
  autoplay?: boolean;
  children: React.ReactNode[] | undefined;
  className?: string;
  dots?: boolean;
  selectedIndex?: number;
  setSelectedIndex?: (arg: number) => void;
  nextButtonClassName?: string;
  prevButtonClassName?: string;
  delay?: number;
}

const BUTTON_STYLES =
  'bg-primary text-white rounded-full h-10 w-10 px-2 drop-shadow-sm ease-in-out duration-200 active:scale-90 focus:outline-hidden focus-visible:outline-hidden';

export default function GenericCarousel({
  options,
  arrows = false,
  fadeEdges = false,
  autoplay = false,
  children,
  className = '',
  dots = true,
  selectedIndex,
  setSelectedIndex,
  nextButtonClassName,
  prevButtonClassName,
  delay,
  ...otherProps
}: GenericCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    options || { loop: true, align: 'center' },
    autoplay ? [Autoplay({ delay: delay ? delay * 1000 : 3000 })] : []
  );
  const [scrollSnaps, setScrollSnaps] = useState<number[]>();

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const onInit = useCallback(() => {
    setScrollSnaps(emblaApi?.scrollSnapList());
  }, [emblaApi]);

  const handleScrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const handleSelect = useCallback(() => {
    if (emblaApi && setSelectedIndex) setSelectedIndex(emblaApi?.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;

    onInit();
    handleSelect();
    emblaApi.on('reInit', handleSelect);
    emblaApi.on('select', handleSelect);
  }, [emblaApi, onInit, handleSelect]);

  return (
    <div
      className={twMerge('carina-generic-carousel relative @lg:mx-auto', className)}
      {...otherProps}
    >
      {/* slight fades to soften the edges of the slider on desktop */}
      {fadeEdges && (
        <>
          <div className="hidden md:block slider-left-overlay bg-linear-to-r from-white to-transparent absolute z-10 h-full w-2" />
          <div className="hidden md:block slider-right-overlay bg-linear-to-l from-white to-transparent absolute z-10 h-full w-2 right-0" />
        </>
      )}

      <div className="carina-carousel overflow-x-clip h-full" ref={emblaRef}>
        <div className="flex h-full relative">{children}</div>
      </div>

      {arrows && (
        <div className="z-20 flex w-full mx-auto justify-between absolute top-1/4">
          <Button
            className={prevButtonClassName || `${BUTTON_STYLES}`}
            onClick={scrollPrev}
            aria-label="Previous image"
          >
            <LuChevronLeft className="h-6 w-6 text-inherit" />
          </Button>

          <Button
            className={nextButtonClassName || `${BUTTON_STYLES}`}
            onClick={scrollNext}
            aria-label="Next image"
          >
            <LuChevronRight className="h-6 w-6 text-inherit" />
          </Button>
        </div>
      )}
      {dots && selectedIndex !== undefined && (
        <div className="carina-generic-carousel__dots bottom-4 group-[.carina-similar-properties]:-bottom-4 flex justify-center absolute w-full">
          {scrollSnaps?.map((snap, index) => (
            <CarouselDot
              key={snap}
              onClick={() => handleScrollTo(index)}
              selected={index === selectedIndex}
              className="group-[.carina-similar-properties]:bg-gray-700"
            />
          ))}
        </div>
      )}
    </div>
  );
}
